/*
 * Authors: mariushirjanu
 * Created: 05/08/2022
 */
import * as _ from 'underscore';
import { util } from '../../../shared/util';
import { ScrollService } from '../../../shared/scroll/scroll.service';
import { UntypedFormGroup } from '@angular/forms';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { GlobalProductService } from '../global-product.service';
import { GaFormlyService, IExtraFieldConfigurationObject, OnChangeHandler } from '../../../shared/formly/ga-formly.service';
import { GlobalProductDataService, IGlobalItem, IProperty, IPropertyGroup } from '../global-product-data.service';
import { DialogSize, GaMessageService, IDialog } from '../../../shared/message/ga-message.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { GLOBAL_PRODUCT_SAVE_ACTION, GlobalProductEditService } from './global-product-edit.service';
import { StateService, Transition, TransitionService } from '@uirouter/angular';
import { STATE_GLOBALPRODUCT_EDIT_GENERAL } from '../../../shared/app-states';
import { IFixedPropertyModel, SpecsNewPropertyDialogComponent } from './specs-new-property-dialog.component';

import { INamedValue, IObject } from '../../../../type_definitions/datatypes';
import { GaWizard, IWizardButton } from '../../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { WIZARD_GLOBAL_PRODUCT_EDIT } from '../../../shared/app-wizards';

interface IGlobalItemExtended extends IGlobalItem {
  _properties?:IObject;
  _freeProperties?:IObject;
  field?:any;
}

@Component({
  selector: 'global-product-edit-specs',
  template: `
    <div class="specs">
      <h2 *ngIf="initialLoad">
        <activity-indicator text="Loading properties"></activity-indicator>
      </h2>

      <h2 *ngIf="!initialLoad">Properties</h2>

      <div *ngIf="!initialLoad && propertyFormHide && fixedPropertyFormHide && freePropertyFormHide" class="empty-properties">
        <i class="fa fa-info-circle"></i> Product {{model.globalPart}} does not have any properties.
      </div>

      <div *ngIf="!initialLoad">
        <formly *ngIf="!propertyFormHide" [form]="fixedPropertyForm" [fields]="fixedPropertyFields" [model]="model" class="propertyform fixedpropertyform"></formly>
        <formly *ngIf="!freePropertyFormHide" [form]="freePropertyForm" [fields]="freePropertyFields" [model]="model" class="propertyform freepropertyform"></formly>
        <fixed-to-bottom innerClass="form-buttons">
          <div class="button-group">
            <button class="btn btn-primary" (click)="addPropertyDialog()">Add new property</button>&nbsp;
            <button class="btn btn-success" (click)="saveProperties()">{{saveButtonLabel}}</button>
          </div>
        </fixed-to-bottom>
      </div>
    </div>
  `
})
export class GlobalProductEditSpecsComponent implements OnInit, OnDestroy {
  private wizard:GaWizard;
  private wizardId:string = WIZARD_GLOBAL_PRODUCT_EDIT;

  public model:IGlobalItemExtended;
  public fixedPropertyForm:UntypedFormGroup = new UntypedFormGroup({});
  public freePropertyForm:UntypedFormGroup = new UntypedFormGroup({});
  public fixedPropertyFields:IExtraFieldConfigurationObject[] = [];
  public freePropertyFields:IExtraFieldConfigurationObject[] = [];
  public initialLoad:boolean = true;
  public saveButtonLabel:string;
  public freePropertyFormHide:boolean;
  public fixedPropertyFormHide:boolean;
  public propertyFormHide:boolean;

  private newPropertyDialog:IDialog;
  private stateAfterSave;
  private labelClasses:string = 'col-xsm-4 col-sm-3 col-md-3 mb-0';
  private valueClasses:string = 'col-xsm-8 col-sm-9 col-md-9';

  constructor(
    private validateService:GaValidateService,
    private globalProductDataService:GlobalProductDataService,
    private formlyService:GaFormlyService,
    private globalProductService:GlobalProductService,
    private globalProductEditService:GlobalProductEditService,
    private messageService:GaMessageService,
    private transitionService:TransitionService,
    private scrollService:ScrollService,
    private stateService:StateService,
    private wizardService:GaWizardService
  ) {
    this.wizard = wizardService.getInstance(this.wizardId);
    this.saveButtonLabel = 'Save Properties';
  }

  public ngOnInit():void {
    this.start();
  }

  private start = ():void => {
    this.model = this.globalProductService.product() || <any>{};
    this.initWizard();
    this.loadProperties();
    this.listen();
  };

  private onDiscard = ():void => {
    this.globalProductService.reset();
    this.fixedPropertyForm.markAsPristine();
    this.freePropertyForm.markAsPristine();
    this.stateService.go(this.stateAfterSave.name, this.stateAfterSave.params);
  };

  private listen():void {
    this.transitionService.onStart({}, (transition:Transition) => {
      // it is possible that wizardService has prevented the event to go through. Then we should do nothing.
      if (event && !event.defaultPrevented && (this.fixedPropertyForm.dirty || this.freePropertyForm.dirty)) {
        this.stateAfterSave = {
          name: transition.to().name,
          params: transition.to().params
        };
        event.preventDefault();
        this.messageService.unsavedChangesDialog(this.onSave, this.onDiscard);
        return Promise.reject('Unsaved changes detected!');
      }
    });
  }

  private loadProperties():void {
    this.globalProductDataService.properties()
      .then((properties:IPropertyGroup[]) => {
        if(properties){
          this.globalProductService.properties(properties);
          this.createFixedFormFields();
          this.createFreeFormFields();
        } else {
          this.freePropertyFormHide = true;
          this.propertyFormHide = true;
        }
      })
      .finally(() => {
        this.initialLoad = false;
      });
  }

  private createFixedFormFields():void {
    this.model._properties = this.model._properties || {};
    this.model.properties = this.model.properties || [];

    const properties:IPropertyGroup[] = this.globalProductService.properties();

    _.each(properties, (propertyList:IPropertyGroup) => {
      let propListModel:IPropertyGroup = _.findWhere(this.model.properties, { name: propertyList.name });

      _.each(propertyList.values, (property:IProperty) => {
        let propModel:IProperty;
        if (propListModel?.values) {
          propModel = _.findWhere(propListModel.values, { name: property.name });
        }

        const isPartOfCategory:boolean = _.findIndex(property.categories, { name: this.model.category?.name }) !== -1;
        if (propModel || isPartOfCategory) {    // propModel=false when retrieved properties (from properties REST API) are not found in the model properties (from product REST API)
          if (!propListModel) {
            propListModel = {
              name: propertyList.name,
              values: []
            };
            this.model.properties.push(propListModel);
          }
          if (!propModel) {
            propModel = <any>{
              control: property.control,
              name: property.name,
              definitionId: property.definitionId
            };
            propListModel.values.push(propModel);
          }

          this.createFixedField(propListModel, propModel);
        }
      });
    });
    this.propertyFormHide = !this.fixedPropertyFields.length;
  }

  private createFreeFormFields():void {
    this.model._freeProperties = this.model._freeProperties || {};
    this.model.freeProperties = this.model.freeProperties || [];

    const titleField:IExtraFieldConfigurationObject = this.formlyService.aTemplate('<h4 class="propListTitle">Free Properties</h4>')
      .build();

    const freePropertyDisclaimer = this.formlyService.aTemplate('<p class="text-warning"><i class="fa fa-warning"></i> Disclaimer: Free properties are for informational purposes only. They cannot be used for reporting or filtering.</p>')
      .build();

    this.freePropertyFields.push(titleField);
    this.freePropertyFields.push(freePropertyDisclaimer);

    this.addFreeProperties(this.model.freeProperties);

    this.freePropertyFormHide = this.freePropertyFields.length <= 2;
  }

  public addPropertyDialog():void {
    this.newPropertyDialog = this.messageService.showDialog({
      component: SpecsNewPropertyDialogComponent,
      wrapperComponent: false,
      inputs: {
        model: this.model
      },
      outputs: {
        onClose: () => {
          this.newPropertyDialog.hide();
        },
        onSave: (properties:IFixedPropertyModel|INamedValue[]) => {
          if(!_.isArray(properties)){
            this.addFixedProperty(<IFixedPropertyModel>properties, true);
          } else {
            this.addFreeProperties(<INamedValue[]>properties, true);
          }
          this.newPropertyDialog.hide();

          setTimeout(() => {
            this.scrollService.scrollToElement('.added');
          });
        }
      },
      size: DialogSize.MD
    });
  }

  public addFixedProperty(fixedProperty:IFixedPropertyModel, newField?:boolean):void {
    let propListModel:IPropertyGroup = _.find(this.model.properties, { name: fixedProperty.choice.list });
    if (!propListModel) {
      propListModel = <any>{
        name: fixedProperty.choice.list,
        values: []
      };
      this.model.properties.push(propListModel);
    }

    // add propModel to propListModel
    const propModel:IProperty = <any>{
      name: fixedProperty.choice.field,
      control: fixedProperty.choice.control,
      definitionId: fixedProperty.choice.definitionId
    };
    if (fixedProperty.field.unit) {
      propModel.unit = this.model.field.unit;
    }
    propListModel.values.push(propModel);

    this.createFixedField(propListModel, propModel, fixedProperty.value, newField);

    // make form visible
    this.propertyFormHide = false;
  }

  private createFixedField(propListModel:IPropertyGroup, propModel:IProperty, value?:string|number|boolean, newField?:boolean):void {
    value = value || propModel.value;

    // create field
    const propertyList:IPropertyGroup = _.findWhere(this.globalProductService.properties(), { name: propListModel.name });
    const property:IProperty = _.findWhere(propertyList.values, { name: propModel.name });

    // set the field's model value
    const key:string = this.globalProductEditService.createFixedPropertyKey(propListModel.name, property.name);
    this.model._properties[key] = value;

    // find (or create) position where to add the field and add it
    let titlePosition:number;
    _.each(this.fixedPropertyFields, (formField:IExtraFieldConfigurationObject, index:number) => {
      if (formField?.templateOptions.name === propListModel.name) {
        titlePosition = index;
      }
    });

    const field:IExtraFieldConfigurationObject = this.globalProductEditService.buildPropertyField(`_properties.${key}`, property.name, property.control, property, propModel, false, this.labelClasses, this.valueClasses, {}, newField);

    // update model value from properties with the field model value
    const onChange:OnChangeHandler = (newValue:string|number):void => {
      const propList:IPropertyGroup = _.findWhere(this.model.properties, { name: propListModel.name });
      const prop:IProperty = _.findWhere(propList.values, { name: propModel.name });
      prop.value = newValue;
    };
    field.templateOptions.onChange = onChange;


    if (util.isNullOrUndefined(titlePosition)) {
      const title:IExtraFieldConfigurationObject = this.formlyService.aTemplate(`<h4 class="propListTitle">${propertyList.name}</h4>`)
        .withExtraOptions({
          templateOptions: {
            id: 'formTitle',
            name: propertyList.name
          }
        })
        .build();
      this.fixedPropertyFields = this.fixedPropertyFields.concat([title, field]);
    } else {
      const nextTitlePosition:number = _.findIndex(this.fixedPropertyFields, (formField:IExtraFieldConfigurationObject, index:number) => index > titlePosition && formField.templateOptions.id === 'formTitle');
      if (nextTitlePosition) {
        this.fixedPropertyFields = this.fixedPropertyFields.concat(this.fixedPropertyFields.splice(nextTitlePosition, 0, field));
      } else {
        this.fixedPropertyFields = this.fixedPropertyFields.concat([field]);
      }
    }

    // update value of properties in the model
    onChange(value);
  }

  public addFreeProperties(freeProperties:INamedValue[], newField?:boolean){
    const newFields:IExtraFieldConfigurationObject[] = [];
    _.each(freeProperties, (property:INamedValue) => {
      // set the field's model value
      const key:string = util.toCamelCase(property.name);
      this.model._freeProperties[key] = property.value;

      if(newField){
        this.model.freeProperties.push(property);
      }

      const onChange:OnChangeHandler = (newValue:string|number):void => {
        const prop:INamedValue = _.findWhere(this.model.freeProperties, { name: property.name });
        prop.value = newValue;
      };

      const field:IExtraFieldConfigurationObject = this.globalProductEditService.buildFreePropertyField(this.model, property, newField, onChange, this.removeFreeProperty);
      newFields.push(field);

      // update value of properties in the model
      onChange(property.value);
    });

    this.freePropertyFields = this.freePropertyFields.concat(newFields);
    this.freePropertyFormHide = false;
  }

  private removeFreeProperty = (model:IGlobalItemExtended, name:string):void => {
    // clear model
    const propertyIndex:number = _.findIndex(model.freeProperties, (prop:INamedValue) => prop.name === name);
    model.freeProperties.splice(propertyIndex, 1);

    // delete temp model property
    const key:string = util.toCamelCase(name);
    delete(model._freeProperties[key]);

    // remove field
    const fieldIndex:number = _.findIndex(this.freePropertyFields, (field:IExtraFieldConfigurationObject) => {
      if (field.templateOptions) {
        return field.templateOptions.name === name;
      }
    });

    this.freePropertyFields.splice(fieldIndex, 1);

    if (this.freePropertyFields.length < 3) {
      this.freePropertyFormHide = true;
    }
  };

  private afterSaved = (updatedProduct:IGlobalItem):IGlobalItem => {
    this.globalProductService.product(updatedProduct);
    this.fixedPropertyForm.markAsPristine();
    this.freePropertyForm.markAsPristine();
    $('.added').removeClass('added');

    if (this.stateAfterSave) {
      this.stateService.go(this.stateAfterSave.name, this.stateAfterSave.params);
    } else {
      // recreate the list of properties and freeProperties to display the ones from the server
      this.fixedPropertyFields = [];
      this.freePropertyFields = [];
      this.model = updatedProduct;
      this.loadProperties();
    }

    return updatedProduct;
  };

  public saveProperties = (): void => {
    this.stateAfterSave = {
      name: STATE_GLOBALPRODUCT_EDIT_GENERAL,
      params: { product: 'willBeReplaced' }
    };
    this.updateProperties();
  };

  private updateProperties():void {
    this.stateAfterSave = undefined;
    this.onSave();
  }

  private onSave = ():Promise<IGlobalItem|void> => {
    //first validate
    return this.validateService.validateFields(this.fixedPropertyFields.concat(this.freePropertyFields), this.model,true)
      .then(() => {
        const model:IGlobalItem = _.omit(util.deepClone(this.model), ['_properties', '_freeProperties']); // remove temporary model properties
        const msg:Noty = this.messageService.showStatusMessage('Saving properties…');
        return this.globalProductDataService.update(model.id, model)
          .then(this.afterSaved)
          .finally(() => {
            this.messageService.closeMessage(msg);
          });
      })
      .catch((errorMessages:string[]) => {
        this.showInvalidMessage(errorMessages);
        return;
      });
  };

  private showInvalidMessage(errorMessages) {
    const title:string = 'Invalid changes!';
    const message:string = '<p><i class="fa fa-warning"></i> There are invalid changes on the product. Please change them before saving.</p>';
    this.messageService.showInvalidFormMessage(title, message, errorMessages);
  }

  private initWizard():void {
    const wizardActions:IWizardButton[] = [];
    wizardActions.push(
      this.wizardService.createActionButton(
        GLOBAL_PRODUCT_SAVE_ACTION,
        this.saveButtonLabel,
        'btn btn-success',
        this.saveProperties
      )
    );

    this.wizard.actions(wizardActions);
  }

  public ngOnDestroy():void {
    this.wizard.clearActions();
  }
}